import { IColumn, IDetailsRowProps, IDropdownOption, Toggle } from '@fluentui/react';
import {
  Button,
  buttonStylesDanger,
  buttonStylesGhost,
  buttonStylesPrimary,
  CommonStyles,
  defaultPalette,
  detailsHeaderStylesBasic,
  DetailsListEditor,
  detailsListStylesBasic,
  FieldType,
  highlightRowBorder,
  IColumnWithDataType,
  IDetailsListEditorProps,
  IEditorProps,
  listCellStylesNoLeftRightPadding,
  ListRow,
  listRowStylesThinWithCellDivider,
  onRenderRequiredColumnHeader,
  toggleStylesEditor,
  useClassNames,
} from '@h2oai/ui-kit';
import { CSSProperties, useCallback, useEffect, useState } from 'react';

import { FeatureType } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/FeatureSetProtoApi.pb';
import {
  FeatureDataTypeWithExtraTypes,
  FeatureSchemaDraftWithDepth,
  SchemaErrorMessage,
} from '../../featurestore/models';
import {
  editFeatureListStylesDefault,
  IEditFeatureListClassNames,
  IEditFeatureListStyles,
} from './EditFeatureList.styles';

const getFeatureDataTypeOptions = (featureType?: FeatureType): IDropdownOption[] => {
  const options: IDropdownOption[] = [
    { key: FeatureDataTypeWithExtraTypes.TinyInt, text: FeatureDataTypeWithExtraTypes.TinyInt },
    { key: FeatureDataTypeWithExtraTypes.SmallInt, text: FeatureDataTypeWithExtraTypes.SmallInt },
    { key: FeatureDataTypeWithExtraTypes.BigInt, text: FeatureDataTypeWithExtraTypes.BigInt },
    { key: FeatureDataTypeWithExtraTypes.Int, text: FeatureDataTypeWithExtraTypes.Int },
    { key: FeatureDataTypeWithExtraTypes.Float, text: FeatureDataTypeWithExtraTypes.Float },
    { key: FeatureDataTypeWithExtraTypes.Double, text: FeatureDataTypeWithExtraTypes.Double },
    { key: FeatureDataTypeWithExtraTypes.String, text: FeatureDataTypeWithExtraTypes.String },
    { key: FeatureDataTypeWithExtraTypes.Binary, text: FeatureDataTypeWithExtraTypes.Binary },
    { key: FeatureDataTypeWithExtraTypes.Boolean, text: FeatureDataTypeWithExtraTypes.Boolean },
    { key: FeatureDataTypeWithExtraTypes.Date, text: FeatureDataTypeWithExtraTypes.Date },
    { key: FeatureDataTypeWithExtraTypes.Timestamp, text: FeatureDataTypeWithExtraTypes.Timestamp },
    { key: FeatureDataTypeWithExtraTypes.Array, text: FeatureDataTypeWithExtraTypes.Array },
    { key: FeatureDataTypeWithExtraTypes.Struct, text: FeatureDataTypeWithExtraTypes.Struct },
  ];
  switch (featureType) {
    case FeatureType.CATEGORICAL:
      return options.filter(
        (option) =>
          option.key !== FeatureDataTypeWithExtraTypes.Array && option.key !== FeatureDataTypeWithExtraTypes.Struct
      );
    case FeatureType.NUMERICAL:
      return options.filter(
        (option) =>
          option.key === FeatureDataTypeWithExtraTypes.TinyInt ||
          option.key === FeatureDataTypeWithExtraTypes.SmallInt ||
          option.key === FeatureDataTypeWithExtraTypes.BigInt ||
          option.key === FeatureDataTypeWithExtraTypes.Int ||
          option.key === FeatureDataTypeWithExtraTypes.Float ||
          option.key === FeatureDataTypeWithExtraTypes.Binary
      );
    case FeatureType.TEMPORAL:
      return options.filter(
        (option) =>
          option.key === FeatureDataTypeWithExtraTypes.Date || option.key === FeatureDataTypeWithExtraTypes.Timestamp
      );
    case FeatureType.TEXT:
      return options.filter((option) => option.key === FeatureDataTypeWithExtraTypes.String);
    case FeatureType.COMPOSITE:
      return options.filter(
        (option) =>
          option.key === FeatureDataTypeWithExtraTypes.Array || option.key === FeatureDataTypeWithExtraTypes.Struct
      );
    default:
      return options;
  }
};

const featureTypeLabelMap = new Map([
  [FeatureType.AUTOMATIC_DISCOVERY, 'Auto'],
  [FeatureType.COMPOSITE, 'Composite'],
  [FeatureType.NUMERICAL, 'Numerical'],
  [FeatureType.CATEGORICAL, 'Categorical'],
  [FeatureType.TEMPORAL, 'Temporal'],
  [FeatureType.TEXT, 'Text'],
]);

const getFeatureTypeOptions = (dataType?: string): IDropdownOption[] => {
  const options: IDropdownOption[] = [
    { key: FeatureType.AUTOMATIC_DISCOVERY, text: 'Auto' },
    { key: FeatureType.COMPOSITE, text: 'Composite' },
    { key: FeatureType.NUMERICAL, text: 'Numerical' },
    { key: FeatureType.CATEGORICAL, text: 'Categorical' },
    { key: FeatureType.TEMPORAL, text: 'Temporal' },
    { key: FeatureType.TEXT, text: 'Text' },
  ];
  switch (dataType) {
    case FeatureDataTypeWithExtraTypes.TinyInt:
    case FeatureDataTypeWithExtraTypes.SmallInt:
    case FeatureDataTypeWithExtraTypes.BigInt:
    case FeatureDataTypeWithExtraTypes.Int:
    case FeatureDataTypeWithExtraTypes.Float:
    case FeatureDataTypeWithExtraTypes.Binary:
      return options.filter(
        (option) =>
          option.key === FeatureType.AUTOMATIC_DISCOVERY ||
          option.key === FeatureType.CATEGORICAL ||
          option.key === FeatureType.NUMERICAL
      );
    case FeatureDataTypeWithExtraTypes.Date:
    case FeatureDataTypeWithExtraTypes.Timestamp:
      return options.filter(
        (option) =>
          option.key === FeatureType.AUTOMATIC_DISCOVERY ||
          option.key === FeatureType.CATEGORICAL ||
          option.key === FeatureType.TEMPORAL
      );
    case FeatureDataTypeWithExtraTypes.Boolean:
      return options.filter(
        (option) => option.key === FeatureType.AUTOMATIC_DISCOVERY || option.key === FeatureType.CATEGORICAL
      );
    case FeatureDataTypeWithExtraTypes.String:
      return options.filter(
        (option) =>
          option.key === FeatureType.AUTOMATIC_DISCOVERY ||
          option.key === FeatureType.CATEGORICAL ||
          option.key === FeatureType.TEXT
      );
    case FeatureDataTypeWithExtraTypes.Array:
      return options.filter((option) => option.key !== FeatureType.COMPOSITE);
    case FeatureDataTypeWithExtraTypes.Struct:
      return options.filter(
        (option) => option.key === FeatureType.AUTOMATIC_DISCOVERY || option.key === FeatureType.COMPOSITE
      );
    default:
      return options;
  }
};

const getToggleColumn = (
  fieldName: string,
  name: string,
  onEndEditFeatureCell: (changed?: boolean, value?: any, rowIndex?: number, fieldName?: string) => void,
  width = 65
): IColumn => ({
  key: fieldName,
  name: name,
  fieldName,
  minWidth: width,
  maxWidth: width + 50,
  data: {
    cellStyles: listCellStylesNoLeftRightPadding,
  },
  onRender: (item: { [key: string]: any } = {}, _index, column) => {
    const fieldName = column?.fieldName || '';
    const checked = !!item[fieldName];
    return (
      <div
        style={{
          ...(CommonStyles.flexCenterVertically as CSSProperties),
          height: '100%',
          width: '100%',
        }}
      >
        <Toggle
          styles={toggleStylesEditor}
          checked={checked}
          onChange={(e, checked) => {
            const cellEl = (e.target as HTMLElement).closest('.h2o-ListCell-root') as HTMLDivElement;
            const dataset = (cellEl as HTMLElement).dataset;
            const rowIndex = +(dataset.rowIndex || -1);
            onEndEditFeatureCell(true, checked, rowIndex, fieldName);
          }}
        />
      </div>
    );
  },
});

const getFeatureListColumns = (
  showSpecialDataColumns = false,
  onAddChild: (e: React.MouseEvent<any>, item: FeatureSchemaDraftWithDepth) => void,
  onDelete: (e: React.MouseEvent<any>, item: FeatureSchemaDraftWithDepth) => void,
  onEndEditFeatureCell: (changed?: boolean, value?: any, rowIndex?: number, fieldName?: string) => void,
  items: FeatureSchemaDraftWithDepth[]
) => {
  const columns: IColumnWithDataType[] = [
    {
      key: 'name',
      name: 'Feature Name',
      fieldName: 'name',
      minWidth: showSpecialDataColumns ? 250 : 300,
      maxWidth: 400,
      data: {
        listCellProps: { editable: true, hasDepth: true },
      },
      onRenderHeader: onRenderRequiredColumnHeader,
    },
    {
      key: 'dataType',
      name: 'Data Type',
      fieldName: 'dataType',
      minWidth: showSpecialDataColumns ? 100 : 120,
      maxWidth: 200,
      onRenderHeader: onRenderRequiredColumnHeader,
      data: {
        listCellProps: { editable: true },
        editorProps: {
          fieldType: FieldType.dropdown,
          fieldProps: {
            placeholder: 'Select Data Type',
          },
        },
      },
    },
    {
      key: 'description',
      name: 'Description',
      fieldName: 'description',
      minWidth: showSpecialDataColumns ? 100 : 150,
      maxWidth: 400,
      data: {
        listCellProps: { editable: true },
      },
    },
    getToggleColumn('isPrimaryKey', 'Primary Key', onEndEditFeatureCell, showSpecialDataColumns ? 65 : 120),
    getToggleColumn('isPartitionColumn', 'Partition Column', onEndEditFeatureCell, showSpecialDataColumns ? 65 : 120),
    {
      key: 'featureType',
      name: 'Feature Type',
      fieldName: 'featureType',
      minWidth: showSpecialDataColumns ? 100 : 120,
      maxWidth: 200,
      onRender: (item) => (
        <div
          className="h2o-ListCell-content"
          style={{
            ...(CommonStyles.flexCenterVertically as CSSProperties),
            height: '100%',
            width: '100%',
            color: defaultPalette.gray400,
          }}
        >
          <div className="h2o-ListCell-text">{featureTypeLabelMap.get(item.featureType)}</div>
        </div>
      ),
      data: {
        listCellProps: { editable: true },
        editorProps: {
          fieldType: FieldType.dropdown,
          fieldProps: {
            placeholder: 'Select Feature Type',
          },
        },
      },
    },
    getToggleColumn('anomalyDetection', 'Anomaly Detection', onEndEditFeatureCell, showSpecialDataColumns ? 65 : 120),
  ];

  if (showSpecialDataColumns) {
    columns.push(
      ...[
        getToggleColumn('nested', 'Nested', onEndEditFeatureCell),
        getToggleColumn('spi', 'SPI', onEndEditFeatureCell),
        getToggleColumn('pci', 'PCI', onEndEditFeatureCell),
        getToggleColumn('rpi', 'RPI', onEndEditFeatureCell),
        getToggleColumn('demographic', 'Demographic', onEndEditFeatureCell, 80),
        getToggleColumn('sensitive', 'Sensitive', onEndEditFeatureCell, 80),
      ]
    );
  }
  columns.push({
    key: 'buttons',
    name: 'Actions',
    minWidth: 231,
    maxWidth: 231,
    onRender: (item, _index, _column, rowIndex) => {
      const { dataType, depthWithHasNextSibling: depth = [], __meta__ } = item;
      const nextDepth = rowIndex !== undefined ? items[rowIndex].depthWithHasNextSibling || [] : [];
      const hasChild = nextDepth.length < depth.length;
      const disabled = __meta__?.data?.disableDeleteButton;
      // array add a child automatically. only struct can add a child.
      const disabledAddChild = dataType !== FeatureDataTypeWithExtraTypes.Struct;
      return (
        <div
          style={{
            ...(CommonStyles.flexCenterVertically as CSSProperties),
            width: '100%',
            textAlign: 'right',
          }}
        >
          <div>
            <Button
              text="Add Child"
              disabled={disabledAddChild}
              styles={buttonStylesGhost}
              onClick={(e) => onAddChild(e, item)}
            />
            <Button
              text="Delete"
              title={hasChild ? 'remove child first' : 'Delete'}
              styles={[buttonStylesDanger, { root: { marginLeft: '8px' } }]}
              disabled={hasChild || disabled}
              onClick={(e) => onDelete(e, item)}
            />
          </div>
        </div>
      );
    },
  });
  return columns;
};

const getEmptyFeature = (depthWithHasNextSibling: boolean[] = [], parentDataType?: string) => ({
  featureType: FeatureType.AUTOMATIC_DISCOVERY,
  depthWithHasNextSibling,
  __meta__: {
    fieldMeta: {
      name: { errorMessage: SchemaErrorMessage.requiredFeatureName },
      dataType: { errorMessage: SchemaErrorMessage.requiredFeatureDataType },
      isPrimaryKey: { disabled: parentDataType === FeatureDataTypeWithExtraTypes.Array },
      isPartitionColumn: { disabled: parentDataType === FeatureDataTypeWithExtraTypes.Array },
    },
  },
});

const getRowIndexByCellEl = (el: HTMLElement) => {
  const cellEl = el.closest('.h2o-ListCell-root') as HTMLDivElement;
  return +(cellEl.dataset.rowIndex || 0);
};

const addChildFeature = (
  rowIndex: number,
  items: FeatureSchemaDraftWithDepth[],
  hasNextIndex = false,
  parentDataType?: string
) => {
  // depthWithHasNextSibling of the previous sibling should be updated
  const depth = items[rowIndex].depthWithHasNextSibling || [];
  const childDepth = [...depth, false];
  const childFeature = { ...getEmptyFeature(childDepth, parentDataType) };
  let nextIndex = rowIndex + 1;
  while (true) {
    const nextFeature = items[nextIndex];
    const nextDepth = nextFeature?.depthWithHasNextSibling || [];
    if (!nextFeature) {
      items.push(childFeature); // done
      break;
    } else {
      if (nextDepth.length === childDepth.length) {
        nextDepth[childDepth.length - 1] = true;
        items[nextIndex].depthWithHasNextSibling = [...nextDepth];
      }
      // insert a child node before the next higher node.
      if (hasNextIndex || nextDepth.length < childDepth.length) {
        items.splice(nextIndex, 0, childFeature); //done
        break;
      }
      nextIndex++;
    }
  }
  return [...items];
};

const removeChildFeature = (rowIndex: number, items: FeatureSchemaDraftWithDepth[]) => {
  const parentDepth = items[rowIndex].depthWithHasNextSibling || [];
  let childrenCount = 0;
  for (let i = rowIndex + 1; i < items.length; i++) {
    const depth = items[i].depthWithHasNextSibling || [];
    if (depth.length > parentDepth.length) {
      childrenCount++;
    } else {
      break;
    }
  }
  if (childrenCount) {
    items.splice(rowIndex + 1, childrenCount);
  }
  return items;
};

const disableCellsExceptDataType = (
  parentDataType: string | undefined,
  feature?: FeatureSchemaDraftWithDepth,
  columns?: IColumnWithDataType[]
) => {
  if (!columns || !feature) return;
  feature.__meta__ = feature.__meta__ || {};
  const fieldMeta = feature.__meta__.fieldMeta || {};
  columns.forEach(({ fieldName }) => {
    if (fieldName && fieldName !== 'dataType') {
      if (parentDataType === FeatureDataTypeWithExtraTypes.Array) {
        (feature as any)[fieldName] = undefined;
      }
      fieldMeta[fieldName] = {
        ...fieldMeta[fieldName],
        disabled: parentDataType === FeatureDataTypeWithExtraTypes.Array,
      };
      if (parentDataType === FeatureDataTypeWithExtraTypes.Array) {
        fieldMeta[fieldName].errorMessage = '';
      }
    }
  });
  // The child row of an array type parent can't be deleted.
  feature.__meta__.data = {
    disableDeleteButton:
      parentDataType === FeatureDataTypeWithExtraTypes.Array || parentDataType === FeatureDataTypeWithExtraTypes.Struct,
  };
  feature.__meta__.fieldMeta = { ...fieldMeta };
};

/**
 * When Data Type is Array or Struct, it will add a new row.
 * Only data type cell can be edited if the parent row data type is Array. Also the child row can be deleted.
 * Parent: Array -> Child: Array -> Grand Child: Int => `Child: Array` will be removed when sending a request and `Parent: Array` will have `Array<Array<Int>>` as a data type instead of `Array`
 */
const applyDataTypeChange = (
  rowIndex: number,
  items: FeatureSchemaDraftWithDepth[],
  fieldName: string,
  oldValue: string,
  value: string,
  columns?: IColumnWithDataType[]
) => {
  if (oldValue === value) {
    return items;
  }
  if (fieldName === 'dataType') {
    if (oldValue === FeatureDataTypeWithExtraTypes.Array || oldValue === FeatureDataTypeWithExtraTypes.Struct) {
      items = removeChildFeature(rowIndex, items);
    }
    if (value === FeatureDataTypeWithExtraTypes.Array || value === FeatureDataTypeWithExtraTypes.Struct) {
      // it can't be a primary key
      items[rowIndex].isPrimaryKey = false;
      items[rowIndex].isPartitionColumn = false;
      updateFieldMeta(items[rowIndex], 'isPrimaryKey', { disabled: true });
      updateFieldMeta(items[rowIndex], 'isPartitionColumn', { disabled: true });
      items = addChildFeature(rowIndex, items, true, value);
    } else {
      const parentDataType = rowIndex > 0 ? items[rowIndex - 1].dataType : '';
      if (parentDataType !== FeatureDataTypeWithExtraTypes.Array) {
        updateFieldMeta(items[rowIndex], 'isPrimaryKey', { disabled: false });
        updateFieldMeta(items[rowIndex], 'isPartitionColumn', { disabled: false });
      }
    }
    disableCellsExceptDataType(items[rowIndex].dataType, items[rowIndex + 1], columns);
  }
  return [...items];
};

const deleteFeature = (rowIndex: number, items: FeatureSchemaDraftWithDepth[]) => {
  const { depthWithHasNextSibling: depth = [] } = items[rowIndex];
  let prevIndex = rowIndex - 1;
  while (true) {
    if (prevIndex < 0) {
      break;
    } else {
      const prevDepth = items[prevIndex].depthWithHasNextSibling || [];
      if (prevDepth.length === depth.length) {
        // doesn't have the next sibling.
        prevDepth[prevDepth.length - 1] = false;
        items[prevIndex].depthWithHasNextSibling = [...prevDepth];
        break;
      }
      if (prevDepth.length < depth.length) {
        break;
      }
    }
    prevIndex--;
  }
  items.splice(rowIndex, 1);
  return [...items];
};

const checkFeatureName = (item: FeatureSchemaDraftWithDepth, name?: string) => {
  const depth = item.depthWithHasNextSibling || [];
  // nested(child) feature name should not have '.'
  return name ? name.replace(depth.length > 0 ? /[.,;{}=\s\n\t]/g : /[,;{}=\s\n\t]/g, '') : name;
};

const validateFeatureIsRequired = (
  items: FeatureSchemaDraftWithDepth[],
  setFeatureListErrorMessage: React.Dispatch<React.SetStateAction<string>>
) => {
  setFeatureListErrorMessage(items.length === 0 ? SchemaErrorMessage.requiredFeature : '');
};

const updateFieldMeta = (
  item: FeatureSchemaDraftWithDepth,
  fieldName: string,
  fieldMeta: {
    errorMessage?: string | undefined;
    changed?: boolean | undefined;
    className?: string | undefined;
    disabled?: boolean | undefined;
  }
) => {
  item.__meta__ = item.__meta__ ? item.__meta__ : {};
  item.__meta__.fieldMeta = item.__meta__.fieldMeta ? item.__meta__.fieldMeta : {};
  item.__meta__.fieldMeta[fieldName] = { ...item.__meta__.fieldMeta[fieldName], ...fieldMeta };
  return item;
};

const getFieldMeta = (item: FeatureSchemaDraftWithDepth, fieldName: string) => {
  item.__meta__ = item.__meta__ ? item.__meta__ : {};
  item.__meta__.fieldMeta = item.__meta__.fieldMeta ? item.__meta__.fieldMeta : {};
  return item.__meta__.fieldMeta[fieldName] || {};
};

const validateFeatures = (
  items: FeatureSchemaDraftWithDepth[],
  columns: IColumn[] = [],
  action: 'Edit' | 'Load' | 'Delete' = 'Edit',
  rowIndex = -1,
  fieldName?: string,
  value?: any,
  oldValue?: any
) => {
  // feature name and data type are required, duplicate feature name, nested feature name should not have '.'
  // Struct and Array can't be a primary key
  if (fieldName === 'dataType') {
    items = applyDataTypeChange(rowIndex, items, fieldName, oldValue, value, columns);
    updateFieldMeta(items[rowIndex], 'dataType', {
      errorMessage: value ? '' : SchemaErrorMessage.requiredFeatureDataType,
    });
  }
  // check feature name is unique
  const featureNameSet = new Set<string>();
  const featureNameSetForTimeTravel = new Set<string>();
  if (fieldName === 'name' || action !== 'Edit') {
    let previousDataType = '';
    let previousDepth: boolean[] = [];
    items.forEach((item, i) => {
      const { dataType, depthWithHasNextSibling: depth = [] } = item;
      let name = item.name;
      if (action === 'Load') {
        name = checkFeatureName(item, name);
        item.name = name;
      }
      const parentIsArray =
        previousDepth.length < depth.length && previousDataType === FeatureDataTypeWithExtraTypes.Array;
      if (parentIsArray) {
        if (action === 'Load') {
          // if the parent is Array, child row should be disabled.
          disableCellsExceptDataType(previousDataType, item, columns);
        }
      } else {
        const nameErrorMessages = [];
        const dataTypeErrorMessages = [];
        if (name) {
          if (featureNameSet.has(name)) {
            nameErrorMessages.push(SchemaErrorMessage.uniqueFeatureName);
          } else {
            // if it is updating name, skip adding the name to check the name is unique later
            if (rowIndex !== i) {
              featureNameSet.add(name);
              if (
                item.dataType !== FeatureDataTypeWithExtraTypes.Array &&
                item.dataType !== FeatureDataTypeWithExtraTypes.Struct &&
                !parentIsArray
              ) {
                featureNameSetForTimeTravel.add(name);
              }
            }
          }
        } else {
          if (!getFieldMeta(item, 'name').disabled) {
            nameErrorMessages.push(SchemaErrorMessage.requiredFeatureName);
          }
        }

        updateFieldMeta(item, 'name', { errorMessage: nameErrorMessages.join(', ') });

        if (action === 'Load') {
          // check data type
          if (!dataType) {
            dataTypeErrorMessages.push(SchemaErrorMessage.requiredFeatureDataType);
          } else {
            if (dataType === FeatureDataTypeWithExtraTypes.Array || dataType === FeatureDataTypeWithExtraTypes.Struct) {
              item.isPrimaryKey = false;
              item.isPartitionColumn = false;
              updateFieldMeta(items[i], 'isPrimaryKey', { disabled: true });
              updateFieldMeta(items[i], 'isPartitionColumn', { disabled: true });
            }
          }
          updateFieldMeta(item, 'dataType', { errorMessage: dataTypeErrorMessages.join(', ') });
        }
      }
      previousDataType = dataType || '';
      previousDepth = [...depth];
    });
  }
  // check duplicate name for the edited name
  if (fieldName === 'name') {
    const item = items[rowIndex];
    if (value) {
      if (featureNameSet.has(value)) {
        const nameFieldMeta = getFieldMeta(item, 'name');
        const errors = [];
        errors.push(SchemaErrorMessage.uniqueFeatureName);
        nameFieldMeta.errorMessage = errors.join(', ');
        updateFieldMeta(item, 'name', nameFieldMeta);
      } else {
        const dataType = item.dataType;
        if (dataType !== FeatureDataTypeWithExtraTypes.Array && dataType !== FeatureDataTypeWithExtraTypes.Struct) {
          featureNameSetForTimeTravel.add(value);
        }
      }
    } else {
      updateFieldMeta(item, 'name', { errorMessage: SchemaErrorMessage.requiredFeatureName });
    }
  }
  return {
    features: [...items],
    featureNameSetForTimeTravel: fieldName === 'name' || !fieldName ? featureNameSetForTimeTravel : undefined,
  };
};

interface IEditFeatureListProps {
  features?: FeatureSchemaDraftWithDepth[];
  draftFeatures?: FeatureSchemaDraftWithDepth[];
  showSpecialData?: boolean;
  onUpdateFeatures?: (items: FeatureSchemaDraftWithDepth[], featureNameSetForTimeTravel?: Set<string>) => void;
}

export const EditFeatureList = ({
  draftFeatures,
  features: rawFeatures,
  showSpecialData,
  onUpdateFeatures,
}: IEditFeatureListProps) => {
  const [items, setItems] = useState<FeatureSchemaDraftWithDepth[]>([]);
  const [optionalEditorFieldProps, setOptionalEditorFieldProps] = useState<{ [key: string]: any }>();
  const [featureListErrorMessage, setFeatureListErrorMessage] = useState('');
  const doUpdateFeatures = useCallback(
    (items: FeatureSchemaDraftWithDepth[], featureNameSet?: Set<string>) => {
      validateFeatureIsRequired(items, setFeatureListErrorMessage);
      if (onUpdateFeatures) {
        onUpdateFeatures(items, featureNameSet);
      }
    },
    [items]
  );
  const onEndEditFeatureCell = useCallback(
    (changed?: boolean, value?: any, rowIndex?: number, fieldName?: string) => {
      if (changed && rowIndex !== undefined && fieldName) {
        if (fieldName === 'name') {
          value = checkFeatureName(items[rowIndex], value);
        }
        const oldValue = (items[rowIndex] as any)[fieldName];
        (items[rowIndex] as any)[fieldName] = value;
        const { features, featureNameSetForTimeTravel } = validateFeatures(
          items,
          featureListProps.columns,
          'Edit',
          rowIndex,
          fieldName,
          value,
          oldValue
        );
        setItems(features);
        doUpdateFeatures(features, featureNameSetForTimeTravel);
      }
    },
    [items]
  );
  const onAddFeature = useCallback(() => {
    const features = [...items, { ...getEmptyFeature() }];
    setItems(features);
    doUpdateFeatures(features);
  }, [items]);
  const onAddChildFeature = useCallback(
    (e: React.MouseEvent) => {
      const features = addChildFeature(getRowIndexByCellEl(e.target as HTMLElement), items);
      setItems(features);
      doUpdateFeatures(features);
    },
    [items]
  );
  const onDeleteFeature = useCallback(
    (e: React.MouseEvent) => {
      const rowIndex = getRowIndexByCellEl(e.target as HTMLElement);
      const newItems = deleteFeature(rowIndex, items);
      const { features, featureNameSetForTimeTravel } = validateFeatures(newItems, featureListProps.columns, 'Delete');
      setItems(features);
      doUpdateFeatures(features, featureNameSetForTimeTravel);
    },
    [items]
  );
  const onBeforeStartEdit = useCallback((editorProps: IEditorProps) => {
    const { item = {}, fieldName } = editorProps.meta || {};
    if (fieldName === 'dataType') {
      const options = getFeatureDataTypeOptions(item.featureType);
      setOptionalEditorFieldProps({
        options,
      });
    } else if (fieldName === 'featureType') {
      const options = getFeatureTypeOptions(item.dataType);
      setOptionalEditorFieldProps({
        options,
      });
    }
  }, []);
  const featureListProps: IDetailsListEditorProps = {
    columns: getFeatureListColumns(showSpecialData, onAddChildFeature, onDeleteFeature, onEndEditFeatureCell, items),
    headerStyles: detailsHeaderStylesBasic,
    styles: detailsListStylesBasic,
    items,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [], itemIndex } = props || {};
      return (
        <ListRow
          data-depth={item.depthWithHasNextSibling ? item.depthWithHasNextSibling.length : 0}
          styles={listRowStylesThinWithCellDivider}
          className={!item.name ? 'is-empty-row' : undefined}
          hideHeader
          data={item}
          columns={columns}
          itemIndex={itemIndex}
          onMouseEnter={(e) => highlightRowBorder(e)}
          onMouseLeave={(e) => highlightRowBorder(e, false)}
        />
      );
    },
    onBeforeStartEdit,
    onEndEdit: onEndEditFeatureCell,
    optionalEditorFieldProps,
  };
  useEffect(() => {
    const { features, featureNameSetForTimeTravel } = validateFeatures(
      draftFeatures || rawFeatures || [],
      featureListProps.columns,
      'Load'
    );
    setItems(features);
    doUpdateFeatures(features, featureNameSetForTimeTravel);
  }, [rawFeatures, draftFeatures]);
  const classNames = useClassNames<IEditFeatureListStyles, IEditFeatureListClassNames>(
    'EditFeatureList',
    editFeatureListStylesDefault
  );
  return (
    <div className={classNames.root} data-test="edit-feature-list">
      <div className={classNames.subTitle}>
        <div className={classNames.subTitleText}>
          Feature List<span className={classNames.subTitleError}>* {featureListErrorMessage}</span>
        </div>
        <div>
          <Button styles={buttonStylesPrimary} iconName="Add" text="Add Feature" onClick={onAddFeature} />
        </div>
      </div>
      <div className={classNames.list}>
        <DetailsListEditor {...featureListProps} />
      </div>
    </div>
  );
};
